import { chooseOpenSale, fetchOpenSales } from '@auctisync/api-client';
import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { EmptyState } from '../../components/bidder-web';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { supabase } from '../../lib/supabase';

/**
 * The one destination the nav can name without knowing which sale is running —
 * and, since 2026-09-14, where `/browse` lands too.
 *
 * The page lives at `/auctions/:slug/live`, which needs a slug the header does
 * not have. This resolves the one a bidder means.
 *
 * 🔴 IT RESOLVES AN **OPEN** SALE, NOT ONLY AN ACTIVE ONE. This used to query
 * `status = 'active'` directly, so a house whose sale was in `preview` got "No
 * sale is running right now" — fine when Browse existed to catch them, and a
 * dead end the moment Browse was merged away, because the catalogue would have
 * become unreachable between sales. `fetchOpenSales` + `chooseOpenSale` are the
 * same two functions Browse used to pick its sale and the phone uses to name
 * one, so all three surfaces answer "which sale" identically.
 *
 * A `?sale=` slug is honoured when it names a sale that is actually open, so
 * the old Browse links keep working; a stale one falls back rather than 404ing.
 */
export function LiveNowRedirect() {
  const { selected } = useBidderHouses();
  const [params] = useSearchParams();
  const [slug, setSlug] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);

  const preferred = params.get('sale');
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const rows = await fetchOpenSales(supabase, selected?.tenant_id ?? null);
      if (cancelled) return;
      setSlug(chooseOpenSale(rows, preferred)?.slug ?? null);
      setResolved(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, preferred]);

  if (!resolved) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <p className="muted">Finding the open sale…</p>
      </div>
    );
  }
  // `replace` so Back returns to wherever the bidder came from rather than to
  // this resolver, which would bounce them forward again.
  //
  // 🔴 THE REST OF THE QUERY TRAVELS WITH IT. `sale` becomes the path and is
  // dropped, but `category` and `q` are read by the merged page's filters, and
  // links like `/browse?category=…` on the home page would otherwise arrive
  // unfiltered — a link that silently stops doing what it says.
  if (slug !== null) {
    const rest = new URLSearchParams(params);
    rest.delete('sale');
    const qs = rest.toString();
    return <Navigate to={`/auctions/${slug}/live${qs === '' ? '' : `?${qs}`}`} replace />;
  }

  return (
    <div className="w-wrap" style={{ padding: '48px 0' }}>
      {/* 🔴 NOT "Browse lots" ANY MORE — that page was merged into this one, so
          the old button pointed back here and would have looped. When no sale
          is open there is genuinely no catalogue to show, and the honest
          destination is the list of sales. */}
      <EmptyState
        icon="⏱"
        title="No sale is open right now"
        body="Lots appear here while a sale is open. Past sales and their results are on the sales page."
        action={
          <Link to="/auctions" className="w-btn w-btn-out">
            See all auctions
          </Link>
        }
      />
    </div>
  );
}
